import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Search, Share2, Trash2, Users, X } from 'lucide-react';

import contactService from '../services/contactService';
import noteService from '../services/noteService';
import teamService from '../services/teamService';
import { avatarInitials } from '../utils/avatar';
import { useLanguage } from '../i18n';
import logger from '../utils/logger';
import { useDropdownBelowModal } from '../hooks/useDropdownBelowModal';
import Modal from './ui/Modal';
import { userErrorMessage } from '../utils/userError';

/**
 * Share a NOTE with a Network contact or a Team (PAI Shared notes #259).
 *
 * A deliberately simpler sibling of `SharedSpaceDialog`: notes have NO roles —
 * a share IS full read+write, which the ticket mandates — so there is no role
 * selector and no owner tier, just "who can edit this with me".
 *
 * Two constraints it inherits from the board/book picker, on purpose:
 *   • it offers ONLY mutually-linked NETWORK contacts and Teams, never a raw
 *     email — the recipient is always someone the user already handshook with;
 *   • it is portaled to <body>, so `fixed inset-0` escapes the panel's
 *     transformed ancestors and renders at real size (the #225 pattern).
 *
 * The confirmation the ticket requires is rendered right here, above the button,
 * so the consequence ("full editing privileges") is unmissable before the click.
 */
const BALL = 'rounded-full bg-white text-pai-void flex items-center justify-center font-bold flex-shrink-0';

const contactEmail = (c) => (c?.omnis_user && c.omnis_user.email) || c?.email || '';

const NoteShareDialog = ({ note, onClose, onChanged, onShared, onRevoked }) => {
    const { t } = useLanguage();
    const [contacts, setContacts] = useState([]);
    const [teams, setTeams] = useState([]);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [sharedWith, setSharedWith] = useState(note?.shared_with || []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [cRes, tRes] = await Promise.all([
                    contactService.listContacts().catch(() => ({ contacts: [] })),
                    teamService.listTeams().catch(() => ({ teams: [] })),
                ]);
                if (cancelled) return;
                setContacts((cRes?.contacts || []).filter((c) => c.tier === 'network' && c.omnis_user_id));
                setTeams(tRes?.teams || []);
            } catch { /* non-fatal — the picker just has fewer options */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Escape closes ONLY this dialog: <Modal> handles it in the capture phase
    // and stops it, so PanelShell's bubble-phase listener never closes the
    // Notes panel behind it (the #225/#272 lesson — Share options #363).

    const alreadyShared = useMemo(
        () => new Set(sharedWith.map((s) => s.user_id)),
        [sharedWith],
    );

    const options = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const contactOpts = contacts
            .filter((c) => !alreadyShared.has(c.omnis_user_id))
            .filter((c) => (c.name || '').toLowerCase().includes(q) || contactEmail(c).toLowerCase().includes(q))
            .map((c) => ({
                type: 'contact',
                id: c.omnis_user_id,
                name: c.name || contactEmail(c),
                email: contactEmail(c),
            }));
        const teamOpts = teams
            .filter((tm) => (tm.name || '').toLowerCase().includes(q))
            .map((tm) => ({ type: 'team', id: tm.id, name: tm.name, count: tm.member_count }));
        return [...contactOpts, ...teamOpts];
    }, [query, contacts, teams, alreadyShared]);

    const showDropdown = !selected && query.trim().length > 0;

    // Portaled below the modal box (#363), same as SharedSpaceDialog — see
    // useDropdownBelowModal's docstring for the root cause this fixes.
    const { modalRef, anchorRef, rect: dropdownRect } = useDropdownBelowModal(showDropdown);

    const pick = (opt) => { setSelected(opt); setQuery(opt.name); setError(''); };
    const onQueryChange = (e) => { setQuery(e.target.value); setSelected(null); };

    const handleShare = async () => {
        if (!selected) return;
        setBusy(true);
        setError('');
        try {
            const res = await noteService.share(note.id,
                selected.type === 'team' ? { teamId: selected.id } : { userId: selected.id });
            setSharedWith((prev) => [...prev, ...(res?.recipients || [])]);
            setSelected(null);
            setQuery('');
            // The detail view (#310) renders the same roster underneath, so it is
            // told directly — its `note` prop was captured when the modal opened.
            onShared?.(res?.recipients || []);
            onChanged?.();
        } catch (err) {
            logger.warn('NOTES', `share failed: ${err?.message || err}`);
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    const handleUnshare = async (userId) => {
        setBusy(true);
        setError('');
        try {
            await noteService.unshare(note.id, userId);
            setSharedWith((prev) => prev.filter((s) => s.user_id !== userId));
            onRevoked?.(userId);
            onChanged?.();
        } catch (err) {
            logger.warn('NOTES', `unshare failed: ${err?.message || err}`);
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    const noun = selected?.type === 'team'
        ? t('noteShareTeamNoun', 'team')
        : t('noteShareContactNoun', 'contact');

    return (
    <>
    <Modal onClose={onClose} zIndex="z-[70]" size="lg" layout="column" cardRef={modalRef} cardClassName="max-h-[85dvh]" scrimTestId="note-share-dialog">
                <div className="flex items-center justify-between px-5 py-4 border-b border-pai-subtle">
                    <h3 data-testid="note-share-title" className="text-base font-semibold text-white flex items-center gap-2">
                        <Share2 size={16} className="text-pai-blue" />
                        {t('noteShareTitle', 'Share Note')}
                    </h3>
                    <button
                        type="button"
                        data-testid="note-share-close"
                        onClick={onClose}
                        aria-label={t('modalCancel', 'Cancel')}
                        className="p-1 text-pai-faint hover:text-white "
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Current collaborators */}
                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-pai-meta mb-2">
                        {t('noteShareCollaborators', 'Shared with')}
                    </div>
                    {sharedWith.length === 0 ? (
                        <div data-testid="note-share-empty" className="text-sm text-pai-meta">
                            {t('noteShareEmpty', 'Not shared yet. Only you can see this note.')}
                        </div>
                    ) : (
                        <div data-testid="note-share-list" className="space-y-2">
                            {sharedWith.map((s) => (
                                <div
                                    key={s.user_id}
                                    data-testid="note-share-row"
                                    className="flex items-center gap-3 rounded-lg border border-pai-subtle p-2"
                                >
                                    <span className={`${BALL} w-8 h-8 text-[11px]`}>
                                        {avatarInitials({ username: s.username, email: s.email })}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm text-white truncate">
                                            {s.username || s.email}
                                        </span>
                                        <span className="block text-[11px] text-pai-meta truncate">
                                            {t('noteShareCanEdit', 'Can view and edit')}
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        data-testid="note-share-remove"
                                        disabled={busy}
                                        onClick={() => handleUnshare(s.user_id)}
                                        title={t('noteShareRemove', 'Remove access')}
                                        aria-label={t('noteShareRemove', 'Remove access')}
                                        className="p-1 text-pai-meta dark:text-white hover:text-red-500 disabled:opacity-50"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Picker */}
                <div className="flex-shrink-0 border-t border-pai-subtle px-5 py-4">
                    <div className="flex items-end gap-2">
                        {/* Results list itself is portaled BELOW the modal (#363)
                            — see the block after this dialog's closing tag. */}
                        <div className="relative flex-1" ref={anchorRef}>
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-pai-faint pointer-events-none" />
                            <input
                                data-testid="note-share-search"
                                type="text"
                                value={query}
                                onChange={onQueryChange}
                                placeholder={t('noteSharePlaceholder', 'Search contact or team...')}
                                className="w-full pl-9 pr-3 py-2.5 text-sm border border-pai rounded-lg bg-white/[0.03] text-white focus:outline-none focus:ring-2 "
                            />
                        </div>
                        <button
                            type="button"
                            data-testid="note-share-add"
                            disabled={!selected || busy}
                            onClick={handleShare}
                            className="px-4 py-2.5 text-sm font-medium bg-white text-pai-void hover:bg-[rgba(74,144,240,0.12)]0 disabled:bg-white/30  disabled:cursor-not-allowed rounded-lg whitespace-nowrap flex items-center gap-1.5"
                        >
                            {busy && <Loader2 size={14} className="animate-spin" />}
                            {t('noteShareBtn', 'Share')}
                        </button>
                    </div>

                    {/* The ticket's mandatory consequence notice — shown BEFORE the
                        click, so nobody grants edit rights without reading it. */}
                    {selected && (
                        <div
                            data-testid="note-share-warning"
                            className="mt-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-[12px] text-amber-800 dark:text-amber-200"
                        >
                            {t('noteShareWarning',
                                `You are about to share this note with this ${noun}. This will give them full editing privileges to modify your note.`)}
                        </div>
                    )}

                    {error && (
                        <div data-testid="note-share-error" className="mt-2 text-[12px] text-rose-600 dark:text-rose-400">
                            {error}
                        </div>
                    )}
                </div>
        </Modal>
    {showDropdown && dropdownRect && createPortal(
        <div
            data-testid="note-share-dropdown"
            style={{
                position: 'fixed',
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
                maxHeight: Math.min(dropdownRect.maxHeight, 240),
            }}
            className="z-[75] overflow-y-auto bg-pai-modal border border-pai rounded-lg shadow-modal-sm py-1"
        >
            {options.length === 0 ? (
                <div data-testid="note-share-no-results" className="px-3 py-2 text-xs text-pai-meta">
                    {t('noteShareNoResults', 'No contacts or teams match. Connect in Contacts first.')}
                </div>
            ) : options.map((opt) => (
                <button
                    key={`${opt.type}:${opt.id}`}
                    type="button"
                    data-testid="note-share-option"
                    data-option-type={opt.type}
                    onClick={() => pick(opt)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[rgba(74,144,240,0.12)] dark:hover:bg-[rgba(74,144,240,0.12)]"
                >
                    {opt.type === 'team' ? (
                        <span className={`${BALL} w-7 h-7 text-[10px]`}><Users size={13} /></span>
                    ) : (
                        <span className={`${BALL} w-7 h-7 text-[10px]`}>
                            {avatarInitials({ username: opt.name, email: opt.email })}
                        </span>
                    )}
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white truncate">{opt.name}</span>
                        <span className="block text-[11px] text-pai-meta truncate">
                            {opt.type === 'team'
                                ? `${t('noteShareTeamLabel', 'Team')} · ${opt.count ?? 0}`
                                : opt.email}
                        </span>
                    </span>
                </button>
            ))}
        </div>,
        document.body,
    )}
    </>
    );
};

export default NoteShareDialog;
